/* eslint-disable ember/no-classic-components, ember/no-observers, ember/require-tagless-components */
import { tracked } from "@glimmer/tracking";
import Component from "@ember/component";
import { hash } from "@ember/helper";
import EmberObject, { action, computed } from "@ember/object";
import didInsert from "@ember/render-modifiers/modifiers/did-insert";
import willDestroy from "@ember/render-modifiers/modifiers/will-destroy";
import { schedule, throttle } from "@ember/runloop";
import { service } from "@ember/service";
import { classNameBindings } from "@ember-decorators/component";
import { observes, on } from "@ember-decorators/object";
import { BasePlugin } from "@uppy/core";
import { resolveAllShortUrls } from "pretty-text/upload-short-url";
import DEditorPreview from "discourse/components/d-editor-preview";
import Wrapper from "discourse/components/form-template-field/wrapper";
import PostTranslationEditor from "discourse/components/post-translation-editor";
import lazyHash from "discourse/helpers/lazy-hash";
import { ajax } from "discourse/lib/ajax";
import { tinyAvatar } from "discourse/lib/avatar-utils";
import { setupComposerPosition } from "discourse/lib/composer/composer-position";
import { decorateImageControls } from "discourse/lib/composer/image-controls";
import { bind, debounce } from "discourse/lib/decorators";
import prepareFormTemplateData from "discourse/lib/form-template-validation";
import {
  fetchUnseenHashtagsInContext,
  linkSeenHashtagsInContext,
} from "discourse/lib/hashtag-decorator";
import { iconHTML } from "discourse/lib/icon-library";
import discourseLater from "discourse/lib/later";
import {
  fetchUnseenMentions,
  linkSeenMentions,
} from "discourse/lib/link-mentions";
import { loadOneboxes } from "discourse/lib/load-oneboxes";
import { generateCookFunction } from "discourse/lib/text";
import { applyValueTransformer } from "discourse/lib/transformer";
import {
  authorizesOneOrMoreImageExtensions,
  IMAGE_MARKDOWN_REGEX,
} from "discourse/lib/uploads";
import UppyComposerUpload from "discourse/lib/uppy/composer-upload";
import { formatUsername } from "discourse/lib/utilities";
import Composer from "discourse/models/composer";
import FormTemplateChooser from "discourse/select-kit/components/form-template-chooser";
import { gt } from "discourse/truth-helpers";
import { applyHtmlDecorators } from "discourse/ui-kit/d-decorated-html";
import DEditor from "discourse/ui-kit/d-editor";
import DPickFilesButton from "discourse/ui-kit/d-pick-files-button";
import { i18n } from "discourse-i18n";

let uploadHandlers = [];
export function addComposerUploadHandler(extensions, method) {
  uploadHandlers.push({
    extensions,
    method,
  });
}
export function cleanUpComposerUploadHandler() {
  // we cannot set this to uploadHandlers = [] because that messes with
  // the references to the original array that the component has. this only
  // really affects tests, but without doing this you could addComposerUploadHandler
  // in a beforeEach function in a test but then it's not adding to the
  // existing reference that the component has, because an earlier test ran
  // cleanUpComposerUploadHandler and lost it. setting the length to 0 empties
  // the array but keeps the reference
  uploadHandlers.length = 0;
}

let uploadPreProcessors = [];
export function addComposerUploadPreProcessor(pluginClass, optionsResolverFn) {
  if (!(pluginClass.prototype instanceof BasePlugin)) {
    throw new Error(
      "Composer upload preprocessors must inherit from the Uppy BasePlugin class."
    );
  }

  uploadPreProcessors.push({
    pluginClass,
    optionsResolverFn,
  });
}
export function cleanUpComposerUploadPreProcessor() {
  uploadPreProcessors = [];
}

let uploadMarkdownResolvers = [];
export function addComposerUploadMarkdownResolver(resolver) {
  uploadMarkdownResolvers.push(resolver);
}
export function cleanUpComposerUploadMarkdownResolver() {
  uploadMarkdownResolvers = [];
}

let apiImageWrapperBtnEvents = [];
export function addApiImageWrapperButtonClickEvent(fn) {
  apiImageWrapperBtnEvents.push(fn);
}

const DEBOUNCE_FETCH_MS = 450;
const DEBOUNCE_JIT_MS = 2000;

@classNameBindings("composer.showToolbar:toolbar-visible", ":wmd-controls")
export default class ComposerEditor extends Component {
  @service composer;
  @service siteSettings;
  @service currentUser;

  @tracked preview;

  composerEventPrefix = "composer";

  fileUploadElementId = "file-uploader";

  init() {
    super.init(...arguments);
    this.warnedCannotSeeMentions = [];
    this.warnedGroupMentions = [];

    this.uppyComposerUpload = new UppyComposerUpload(this, {
      composerEventPrefix: this.composerEventPrefix,
      composerModel: this.composer.model,
      uploadMarkdownResolvers,
      uploadPreProcessors,
      uploadHandlers,
      fileUploadElementId: this.fileUploadElementId,
    });
  }

  @computed("composer.formTemplateIds")
  get selectedFormTemplateId() {
    if (this._selectedFormTemplateId) {
      return this._selectedFormTemplateId;
    }

    return (
      this.composer.model.formTemplateId || this.composer.formTemplateIds?.[0]
    );
  }

  set selectedFormTemplateId(value) {
    this._selectedFormTemplateId = value;
  }

  get composerRedesign() {
    return this.siteSettings.enable_composer_redesign;
  }

  get topic() {
    return this.composer.get("model.topic");
  }

  @computed(
    "composer.model.requiredCategoryMissing",
    "currentUser.useRichEditor"
  )
  get replyPlaceholder() {
    let placeholder = "composer.reply_placeholder_choose_category";

    if (!this.composer?.model?.requiredCategoryMissing) {
      const allowImages = authorizesOneOrMoreImageExtensions(
        this.currentUser.staff,
        this.siteSettings
      );

      let key;
      if (this.currentUser.useRichEditor) {
        key = allowImages
          ? "reply_placeholder_rte"
          : "reply_placeholder_rte_no_images";
      } else {
        key = allowImages ? "reply_placeholder" : "reply_placeholder_no_images";
      }

      placeholder = `composer.${key}`;
    }

    return i18n(
      applyValueTransformer("composer-editor-reply-placeholder", placeholder, {
        model: this.composer,
      })
    );
  }

  @computed
  get showLink() {
    return this.currentUser && this.currentUser.link_posting_access !== "none";
  }

  @computed
  get markdownOptions() {
    return {
      previewing: true,

      formatUsername,

      lookupAvatarByPostNumber: (postNumber, topicId) => {
        const avatarTemplate = this.lookupAvatarTemplateByPostNumber(
          postNumber,
          topicId
        );

        return avatarTemplate ? tinyAvatar(avatarTemplate) : undefined;
      },

      lookupAvatarTemplateByPostNumber: this.lookupAvatarTemplateByPostNumber,

      lookupPrimaryUserGroupByPostNumber: (postNumber, topicId) => {
        const topic = this.topic;
        if (!topic) {
          return;
        }

        const posts = topic.get("postStream.posts");
        if (posts && topicId === topic.get("id")) {
          const quotedPost = posts.find((p) => p.post_number === postNumber);
          if (quotedPost) {
            return quotedPost.primary_group_name;
          }
        }
      },

      hashtagTypesInPriorityOrder:
        this.site.hashtag_configurations["topic-composer"],
      hashtagIcons: this.site.hashtag_icons,
    };
  }

  @computed(
    "composer.model.reply",
    "composer.model.replyLength",
    "composer.model.missingReplyCharacters",
    "composer.model.minimumPostLength",
    "composer.lastValidatedAt"
  )
  get validation() {
    const postType = this.get("composer.post.post_type");
    if (postType === this.site.get("post_types.small_action")) {
      return;
    }

    let reason;
    if (this.composer?.model?.missingReplyCharacters > 0) {
      if (this.composer?.model?.replyLength < 1) {
        reason = i18n("composer.error.post_missing");
      } else {
        reason = i18n("composer.error.post_length", {
          count: this.composer?.model?.minimumPostLength,
        });
        const tl = this.get("currentUser.trust_level");
        if ((tl === 0 || tl === 1) && !this._isNewTopic) {
          reason +=
            "<br/>" +
            i18n("composer.error.try_like", {
              heart: iconHTML("heart", {
                label: i18n("likes_lowercase", { count: 1 }),
              }),
            });
        }
      }
    }

    if (reason) {
      return EmberObject.create({
        failed: true,
        reason,
        lastShownAt: this.composer?.lastValidatedAt,
      });
    }
  }

  @computed("composer.{creatingTopic,editingFirstPost,creatingSharedDraft}")
  get _isNewTopic() {
    return (
      this.composer.model.creatingTopic ||
      this.composer.model.editingFirstPost ||
      this.composer.model.creatingSharedDraft
    );
  }

  get showTranslationEditor() {
    const post = this.get("composer.model.post");
    if (!post?.can_localize_post) {
      return false;
    }

    if (this.composer.model?.action === Composer.ADD_TRANSLATION) {
      return true;
    }

    return false;
  }

  @computed(
    "composer.formTemplateIds",
    "composer.model.replyingToTopic",
    "composer.model.editingPost"
  )
  get showFormTemplateForm() {
    return (
      this.composer?.formTemplateIds?.length > 0 &&
      !this.composer?.model?.replyingToTopic &&
      !this.composer?.model?.editingPost
    );
  }

  @computed("composer.model")
  get forceEditorMode() {
    return applyValueTransformer("composer-force-editor-mode", null, {
      model: this.composer.model,
    });
  }

  @observes("composer.focusTarget")
  setFocus() {
    if (this.composer.focusTarget === "editor") {
      this.textManipulation.putCursorAtEnd();
    }
  }

  @bind
  lookupAvatarTemplateByPostNumber(postNumber, topicId) {
    const topic = this.topic;
    if (!topic || topicId !== topic.get("id")) {
      return;
    }

    const quotedPost = topic
      .get("postStream.posts")
      ?.find((p) => p.post_number === postNumber);

    if (!quotedPost) {
      return;
    }

    return applyValueTransformer(
      "composer-editor-quoted-post-avatar-template",
      quotedPost.get("avatar_template"),
      { post: quotedPost }
    );
  }

  /**
   * Sets up the editor with the given text manipulation instance
   *
   * @param {TextManipulation} textManipulation The text manipulation instance
   * @returns {(() => void)} destructor function
   */
  @bind
  setupEditor(textManipulation) {
    this.textManipulation = textManipulation;
    this.uppyComposerUpload.textManipulation = textManipulation;

    const input = this.element.querySelector(".d-editor-input");

    input.addEventListener("scroll", this._throttledSyncEditorAndPreviewScroll);

    this.composer.set("allowPreview", this.textManipulation.allowPreview);

    if (
      // Focus on the editor unless we have a title
      !this.get("composer.model.canEditTitle") ||
      // Or focus is in the body (e.g. when the editor is destroyed)
      document.activeElement.tagName === "BODY"
    ) {
      this.textManipulation.putCursorAtEnd();
    }

    const destroyComposerPosition = setupComposerPosition(input, {
      swipeToCollapse: this.siteSettings.enable_composer_redesign,
    });

    return () => {
      destroyComposerPosition();

      input.removeEventListener(
        "scroll",
        this._throttledSyncEditorAndPreviewScroll
      );
    };
  }

  resetImageControls(buttonWrapper) {
    const imageResize = buttonWrapper.querySelector(".scale-btn-container");
    const imageDelete = buttonWrapper.querySelector(".delete-image-button");

    const readonlyContainer = buttonWrapper.querySelector(
      ".alt-text-readonly-container"
    );
    const editContainer = buttonWrapper.querySelector(
      ".alt-text-edit-container"
    );

    imageResize.removeAttribute("hidden");
    imageDelete.removeAttribute("hidden");

    readonlyContainer.removeAttribute("hidden");
    buttonWrapper.removeAttribute("editing");
    editContainer.setAttribute("hidden", "true");
  }

  commitAltText(buttonWrapper) {
    const index = parseInt(buttonWrapper.getAttribute("data-image-index"), 10);
    const matchingPlaceholder = this.get("composer.model.reply").match(
      IMAGE_MARKDOWN_REGEX
    );
    const match = matchingPlaceholder?.[index];

    if (!match) {
      this.resetImageControls(buttonWrapper);
      return;
    }

    const input = buttonWrapper.querySelector("input.alt-text-input");
    const replacement = match.replace(
      IMAGE_MARKDOWN_REGEX,
      `![${input.value}|$2$3$4]($5)`
    );

    this.appEvents.trigger(
      `${this.composerEventPrefix}:replace-text`,
      match,
      replacement
    );

    this.resetImageControls(buttonWrapper);
  }

  @action
  onExpandPopupMenuOptions(toolbarEvent) {
    const selected = toolbarEvent.selected;
    toolbarEvent.selectText(selected.start, selected.end - selected.start);
    this.composer.storeToolbarState(toolbarEvent);

    window.getSelection().removeAllRanges();
  }

  showPreview() {
    this.composer.togglePreview();
  }

  @action
  extraButtons(toolbar) {
    const composerRedesign = this.siteSettings.enable_composer_redesign;

    if (
      this.composer.allowUpload &&
      this.composer.uploadIcon &&
      (composerRedesign || this.site.desktopView)
    ) {
      toolbar.addButton({
        id: "upload",
        group: "insertions",
        icon: this.composer.uploadIcon,
        title: "upload",
        sendAction: this.showUploadModal,
      });
    }

    toolbar.addButton({
      id: "options",
      group: "extras",
      icon: composerRedesign ? "discourse-circle-plus" : "circle-plus",
      title: "composer.options",
      sendAction: this.onExpandPopupMenuOptions.bind(this),
      popupMenu: {
        options: () => this.composer.popupMenuOptions,
        action: this.composer.onPopupMenuAction,
      },
    });
  }

  @action
  replyChanged() {
    this.appEvents.trigger("composer:reply-changed", this.composer.model);
  }

  @action
  previewUpdated(preview, helper) {
    decorateImageControls(preview);

    this._renderMentions(preview);
    this._renderHashtags(preview);
    this._refreshOneboxes(preview);
    this._expandShortUrls(preview);

    this._decorateCookedElement(preview, helper);

    this.composer.afterRefresh(preview);
  }

  @action
  async updateFormPreview() {
    const formTemplateData = prepareFormTemplateData(
      document.querySelector("#form-template-form"),
      this.composer.selectedFormTemplate,
      false
    );

    this.composer.model.set("reply", formTemplateData);

    this.preview = await this.cachedCookAsync(
      formTemplateData,
      this.markdownOptions
    );
  }

  async cachedCookAsync(text, options) {
    this._cachedCookFunction ||= await generateCookFunction(options || {});
    return await this._cachedCookFunction(text);
  }

  @action
  updateSelectedFormTemplateId(formTemplateId) {
    this.selectedFormTemplateId = formTemplateId;
  }

  @action
  showUploadModal() {
    document.getElementById(this.fileUploadElementId).click();
  }

  @on("didInsertElement")
  _composerEditorInit() {
    this.appEvents.trigger(`${this.composerEventPrefix}:will-open`);
  }

  @on("willDestroyElement")
  _composerClosed() {
    this.appEvents.trigger(`${this.composerEventPrefix}:will-close`);

    // need to wait a bit for the "slide down" transition of the composer
    discourseLater(
      () => this.appEvents.trigger(`${this.composerEventPrefix}:closed`),
      400
    );
  }

  @action
  _composerEditorInitPreview(elem) {
    const preview = elem.classList.contains("d-editor-preview-wrapper")
      ? elem
      : elem.querySelector(".d-editor-preview-wrapper");
    this._registerImageAltTextButtonClick(preview);
    this._editorInitPreview = true;
  }

  @action
  _composerEditorDestroyPreview(elem) {
    const preview = elem.classList.contains("d-editor-preview-wrapper")
      ? elem
      : elem.querySelector(".d-editor-preview-wrapper");

    if (preview) {
      preview.removeEventListener(
        "click",
        this._handleAltTextCancelButtonClick
      );
      preview.removeEventListener("click", this._handleAltTextEditButtonClick);
      preview.removeEventListener("click", this._handleAltTextOkButtonClick);
      preview.removeEventListener("click", this._handleImageDeleteButtonClick);
      preview.removeEventListener("click", this._handleImageGridButtonClick);
      preview.removeEventListener("click", this._handleImageScaleButtonClick);
      preview.removeEventListener("keypress", this._handleAltTextInputKeypress);

      apiImageWrapperBtnEvents.forEach((fn) =>
        preview.removeEventListener("click", fn)
      );
    }
  }

  @action
  _composerEditorInitEditor(elem) {
    if (this.composer.allowUpload) {
      this.uppyComposerUpload.setup(elem);
    }
  }

  @action
  _composerEditorDestroyEditor(elem) {
    if (this.composer.allowUpload && this._cleanupComposerUploadElement) {
      this.uppyComposerUpload.teardown(elem);
    }
  }

  @action
  _composerEditorInitFormTemplate(formEl) {
    if (this.composer.allowUpload) {
      this.uppyComposerUpload.setup(formEl);
    }
  }

  @bind
  _throttledSyncEditorAndPreviewScroll(event) {
    const preview = this.element.querySelector(".d-editor-preview-wrapper");
    throttle(this, this._syncEditorAndPreviewScroll, event.target, preview, 20);
  }

  _syncEditorAndPreviewScroll(input, preview) {
    if (!input || !preview) {
      return;
    }

    if (input.scrollTop === 0) {
      preview.scrollTop = 0;
      return;
    }

    const inputHeight = input.scrollHeight;
    const previewHeight = preview.scrollHeight;

    if (input.clientHeight + input.scrollTop + 100 > inputHeight) {
      // cheat, special case for bottom
      preview.scrollTop = previewHeight;
      return;
    }

    const factor = previewHeight / inputHeight;
    preview.scrollTop = input.scrollTop * factor + 50;
  }

  _renderMentions(preview) {
    const unseen = linkSeenMentions(preview, this.siteSettings);
    if (unseen.length > 0) {
      this._renderUnseenMentions(preview, unseen);
    } else {
      this._warnMentionedGroups(preview);
      this._warnCannotSeeMention(preview);
    }
  }

  @debounce(DEBOUNCE_FETCH_MS)
  _renderUnseenMentions(preview, unseen) {
    fetchUnseenMentions({
      names: unseen,
      topicId: this.get("composer.model.topic.id"),
      allowedNames: this.get("composer.model.targetRecipients")?.split(","),
    }).then((response) => {
      linkSeenMentions(preview, this.siteSettings);
      this._warnMentionedGroups(preview);
      this._warnCannotSeeMention(preview);
      this._warnHereMention(response.here_count);
    });
  }

  _renderHashtags(preview) {
    const context = this.site.hashtag_configurations["topic-composer"];
    const unseen = linkSeenHashtagsInContext(context, preview);
    if (unseen.length > 0) {
      this._renderUnseenHashtags(preview, unseen, context);
    }
  }

  @debounce(DEBOUNCE_FETCH_MS)
  _renderUnseenHashtags(preview, unseen, context) {
    fetchUnseenHashtagsInContext(context, unseen).then(() =>
      linkSeenHashtagsInContext(context, preview)
    );
  }

  @debounce(DEBOUNCE_FETCH_MS)
  _refreshOneboxes(preview) {
    const post = this.get("composer.post");
    // If we are editing a post, we'll refresh its contents once.
    const refresh = post && !post.get("refreshedPost");

    const loaded = loadOneboxes(
      preview,
      ajax,
      this.get("composer.model.topic.id"),
      this.get("composer.model.category.id"),
      this.siteSettings.max_oneboxes_per_post,
      refresh,
      false,
      (onebox) => onebox && this.appEvents.trigger("composer:refresh-preview")
    );

    if (refresh && loaded > 0) {
      post.set("refreshedPost", true);
    }
  }

  _expandShortUrls(preview) {
    resolveAllShortUrls(ajax, this.siteSettings, preview);
  }

  _decorateCookedElement(preview, helper) {
    applyHtmlDecorators(preview, helper);
  }

  @debounce(DEBOUNCE_JIT_MS)
  _warnMentionedGroups(preview) {
    schedule("afterRender", () => {
      preview
        .querySelectorAll(".mention-group[data-mentionable-user-count]")
        .forEach((mention) => {
          const { name } = mention.dataset;
          if (
            this.warnedGroupMentions.includes(name) ||
            this._isInQuote(mention)
          ) {
            return;
          }

          this.warnedGroupMentions.push(name);
          this.composer.groupsMentioned({
            name,
            userCount: mention.dataset.mentionableUserCount,
            maxMentions: mention.dataset.maxMentions,
          });
        });
    });
  }

  // add a delay to allow for typing, so you don't open the warning right away
  // previously we would warn after @bob even if you were about to mention @bob2
  @debounce(DEBOUNCE_JIT_MS)
  _warnCannotSeeMention(preview) {
    if (this.composer.model?.draftKey === Composer.NEW_PRIVATE_MESSAGE_KEY) {
      return;
    }

    preview.querySelectorAll(".mention[data-reason]").forEach((mention) => {
      const { name } = mention.dataset;
      if (this.warnedCannotSeeMentions.includes(name)) {
        return;
      }

      this.warnedCannotSeeMentions.push(name);
      this.composer.cannotSeeMention({
        name,
        reason: mention.dataset.reason,
      });
    });

    preview
      .querySelectorAll(".mention-group[data-reason]")
      .forEach((mention) => {
        const { name } = mention.dataset;
        if (this.warnedCannotSeeMentions.includes(name)) {
          return;
        }

        this.warnedCannotSeeMentions.push(name);
        this.composer.cannotSeeMention({
          name,
          reason: mention.dataset.reason,
          notifiedCount: mention.dataset.notifiedUserCount,
          isGroup: true,
        });
      });
  }

  _warnHereMention(hereCount) {
    if (!hereCount || hereCount === 0) {
      return;
    }

    this.composer.hereMention(hereCount);
  }

  @bind
  _handleImageScaleButtonClick(event) {
    if (!event.target.classList.contains("scale-btn")) {
      return;
    }

    const index = parseInt(
      event.target.closest(".button-wrapper").dataset.imageIndex,
      10
    );

    const scale = event.target.dataset.scale;
    const matchingPlaceholder = this.get("composer.model.reply").match(
      IMAGE_MARKDOWN_REGEX
    );

    if (matchingPlaceholder) {
      const match = matchingPlaceholder[index];

      if (match) {
        const replacement = match.replace(
          IMAGE_MARKDOWN_REGEX,
          `![$1|$2, ${scale}%$4]($5)`
        );

        this.appEvents.trigger(
          `${this.composerEventPrefix}:replace-text`,
          matchingPlaceholder[index],
          replacement,
          { regex: IMAGE_MARKDOWN_REGEX, index }
        );
      }
    }

    event.preventDefault();
    return;
  }

  @bind
  _handleAltTextInputKeypress(event) {
    if (!event.target.classList.contains("alt-text-input")) {
      return;
    }

    if (event.key === "[" || event.key === "]") {
      event.preventDefault();
    }

    if (event.key === "Enter") {
      const buttonWrapper = event.target.closest(".button-wrapper");
      this.commitAltText(buttonWrapper);
    }
  }

  @bind
  _handleAltTextEditButtonClick(event) {
    if (!event.target.classList.contains("alt-text-edit-btn")) {
      return;
    }

    const buttonWrapper = event.target.closest(".button-wrapper");
    const imageResize = buttonWrapper.querySelector(".scale-btn-container");
    const imageDelete = buttonWrapper.querySelector(".delete-image-button");

    const readonlyContainer = buttonWrapper.querySelector(
      ".alt-text-readonly-container"
    );
    const altText = readonlyContainer.querySelector(".alt-text");

    const editContainer = buttonWrapper.querySelector(
      ".alt-text-edit-container"
    );
    const editContainerInput = editContainer.querySelector(".alt-text-input");

    buttonWrapper.setAttribute("editing", "true");
    imageResize.setAttribute("hidden", "true");
    imageDelete.setAttribute("hidden", "true");
    readonlyContainer.setAttribute("hidden", "true");
    editContainerInput.value = altText.textContent;
    editContainer.removeAttribute("hidden");
    editContainerInput.focus();
    event.preventDefault();
  }

  @bind
  _handleAltTextOkButtonClick(event) {
    if (!event.target.classList.contains("alt-text-edit-ok")) {
      return;
    }

    const buttonWrapper = event.target.closest(".button-wrapper");
    this.commitAltText(buttonWrapper);
  }

  @bind
  _handleAltTextCancelButtonClick(event) {
    if (!event.target.classList.contains("alt-text-edit-cancel")) {
      return;
    }

    const buttonWrapper = event.target.closest(".button-wrapper");
    this.resetImageControls(buttonWrapper);
  }

  @bind
  _handleImageDeleteButtonClick(event) {
    if (!event.target.classList.contains("delete-image-button")) {
      return;
    }
    const index = parseInt(
      event.target.closest(".button-wrapper").dataset.imageIndex,
      10
    );
    const matchingPlaceholder = this.get("composer.model.reply").match(
      IMAGE_MARKDOWN_REGEX
    );

    if (!matchingPlaceholder?.[index]) {
      return;
    }

    this.appEvents.trigger(
      `${this.composerEventPrefix}:replace-text`,
      matchingPlaceholder[index],
      "",
      { regex: IMAGE_MARKDOWN_REGEX, index }
    );
  }

  @bind
  _handleImageGridButtonClick(event) {
    if (!event.target.classList.contains("wrap-image-grid-button")) {
      return;
    }

    const index = parseInt(
      event.target.closest(".button-wrapper").dataset.imageIndex,
      10
    );
    const reply = this.get("composer.model.reply");
    const matches = reply.match(IMAGE_MARKDOWN_REGEX);
    const closingIndex =
      index + parseInt(event.target.dataset.imageCount, 10) - 1;

    if (!matches?.[index] || !matches[closingIndex]) {
      return;
    }

    const textArea = this.element.querySelector(".d-editor-input");
    textArea.selectionStart = reply.indexOf(matches[index]);
    textArea.selectionEnd =
      reply.indexOf(matches[closingIndex]) + matches[closingIndex].length;

    this.appEvents.trigger(
      `${this.composerEventPrefix}:apply-surround`,
      "[grid]",
      "[/grid]",
      "grid_surround",
      { useBlockMode: true }
    );
  }

  _registerImageAltTextButtonClick(preview) {
    if (!preview) {
      return;
    }

    preview.addEventListener("click", this._handleAltTextCancelButtonClick);
    preview.addEventListener("click", this._handleAltTextEditButtonClick);
    preview.addEventListener("click", this._handleAltTextOkButtonClick);
    preview.addEventListener("click", this._handleImageDeleteButtonClick);
    preview.addEventListener("click", this._handleImageGridButtonClick);
    preview.addEventListener("click", this._handleImageScaleButtonClick);
    preview.addEventListener("keypress", this._handleAltTextInputKeypress);

    apiImageWrapperBtnEvents.forEach((fn) =>
      preview.addEventListener("click", fn)
    );
  }

  _isInQuote(element) {
    let parent = element.parentElement;
    while (parent && !this._isPreviewRoot(parent)) {
      if (this._isQuote(parent)) {
        return true;
      }

      parent = parent.parentElement;
    }

    return false;
  }

  _isPreviewRoot(element) {
    return (
      element.tagName === "DIV" &&
      element.classList.contains("d-editor-preview")
    );
  }

  _isQuote(element) {
    return element.tagName === "ASIDE" && element.classList.contains("quote");
  }

  <template>
    {{#if this.showFormTemplateForm}}
      <div class="d-editor">
        <div class="d-editor-textarea-column">
          {{yield}}

          {{#if (gt this.composer.formTemplateIds.length 1)}}
            <FormTemplateChooser
              class="composer-select-form-template"
              @filteredIds={{this.composer.formTemplateIds}}
              @onChange={{this.updateSelectedFormTemplateId}}
              @options={{hash maximum=1}}
              @value={{this.selectedFormTemplateId}}
            />
          {{/if}}
          <form
            id="form-template-form"
            {{didInsert this._composerEditorInitFormTemplate}}
          >
            <Wrapper
              @id={{this.selectedFormTemplateId}}
              @initialValues={{this.composer.formTemplateInitialValues}}
              @onChange={{this.updateFormPreview}}
              @onSelectFormTemplate={{this.composer.onSelectFormTemplate}}
              @uppyComposerUpload={{this.uppyComposerUpload}}
            />
          </form>
        </div>
        {{#if this.siteSettings.show_preview_for_form_templates}}
          <DEditorPreview
            @forcePreview={{this.forcePreview}}
            @onPreviewUpdated={{this.previewUpdated}}
            @outletArgs={{this.outletArgs}}
            @preview={{this.preview}}
            {{didInsert this._composerEditorInitPreview}}
            {{willDestroy this._composerEditorDestroyPreview}}
          />
        {{/if}}
      </div>
    {{else if this.showTranslationEditor}}
      <PostTranslationEditor
        @extraButtons={{this.extraButtons}}
        @setupEditor={{this.setupEditor}}
        @showLink={{this.showLink}}
        @uppyComposerUpload={{this.uppyComposerUpload}}
        {{didInsert this._composerEditorInitPreview}}
        {{willDestroy this._composerEditorDestroyPreview}}
      />
    {{else}}
      <DEditor
        @categoryId={{this.composer.model.category.id}}
        @change={{this.replyChanged}}
        @composerEvents={{true}}
        @disabled={{this.composer.disableTextarea}}
        @disableSubmit={{this.composer.disableSubmit}}
        @extraButtons={{this.extraButtons}}
        @forceEditorMode={{this.forceEditorMode}}
        @forcePreview={{this.forcePreview}}
        @loading={{this.composer.loading}}
        @markdownOptions={{this.markdownOptions}}
        @onPopupMenuAction={{this.composer.onPopupMenuAction}}
        @onSetup={{this.setupEditor}}
        @outletArgs={{lazyHash
          composer=this.composer.model
          editorType="composer"
        }}
        @placeholder={{this.replyPlaceholder}}
        @popupMenuOptions={{this.composer.popupMenuOptions}}
        @previewUpdated={{this.previewUpdated}}
        @processPreview={{this.composer.isPreviewVisible}}
        @renderYieldAboveContainer={{this.composerRedesign}}
        @replyingToUserId={{this.composer.replyingToUserId}}
        @showLink={{this.showLink}}
        @toolbarPortalTarget={{this.toolbarPortalTarget}}
        @topicId={{this.composer.model.topic.id}}
        @userToolbarPreferences={{true}}
        @validation={{this.validation}}
        @value={{this.composer.model.reply}}
        {{didInsert this._composerEditorInitEditor}}
        {{willDestroy this._composerEditorDestroyEditor}}
        {{didInsert this._composerEditorInitPreview}}
        {{willDestroy this._composerEditorDestroyPreview}}
      >
        {{yield}}
      </DEditor>
    {{/if}}

    {{#if this.composer.allowUpload}}
      <DPickFilesButton
        name="file-uploader"
        @allowMultiple={{true}}
        @fileInputId={{this.fileUploadElementId}}
      />
    {{/if}}
  </template>
}
